import Vue from 'vue';
import { createCloudPreference } from './cloud-preference';

const key = 'simple-music.equalizer.v1';
export const eqFrequencies = [60, 170, 310, 600, 1000, 3000, 6000, 12000, 14000, 16000];
export const eqPresets: Record<string, { label: string; gains: number[] }> = {
  flat: { label: '平直', gains: [0,0,0,0,0,0,0,0,0,0] },
  bass: { label: '低音澎湃', gains: [6,5,3,1,0,0,0,0,0,0] },
  vocal: { label: '清澈人声', gains: [-2,-1,0,2,4,4,2,1,0,0] },
  treble: { label: '明亮', gains: [-1,-1,0,0,0,1,2,3,3,2] },
};
function read() {
  try {
    const data = JSON.parse(localStorage.getItem(key) || '{}');
    const gains = Array.isArray(data.gains) && data.gains.length === eqFrequencies.length && data.gains.every((v: unknown) => typeof v === 'number' && Number.isFinite(v)) ? data.gains.map((v: number) => Math.max(-12, Math.min(12, v))) : [...eqPresets.flat.gains];
    return { preset: typeof data.preset === 'string' && (data.preset === 'custom' || eqPresets[data.preset]) ? data.preset : 'flat', gains };
  } catch { return { preset: 'flat', gains: [...eqPresets.flat.gains] }; }
}
const saved = read();
export const equalizer = Vue.observable({ enabled: false, active: false, message: '音效适用于本地文件和站内中转；直连播放保持原音。', ...saved });
let context: AudioContext | null = null;
type Graph = { media: HTMLAudioElement; source: MediaElementAudioSourceNode; filters: BiquadFilterNode[]; preamp: GainNode };
const graphs = new Map<HTMLAudioElement, Graph>();
let owner: HTMLAudioElement | null = null;
const bound = new WeakSet<HTMLAudioElement>();
type EqPreference = { preset: string; gains: number[] };
const validateEq = (value: any): EqPreference => {
  if (!value || !['flat','bass','vocal','treble','custom'].includes(value.preset) || !Array.isArray(value.gains) || value.gains.length !== eqFrequencies.length || value.gains.some((v: unknown) => typeof v !== 'number' || !Number.isFinite(v) || v < -12 || v > 12)) throw new Error('云端音效设置无效');
  return { preset: value.preset, gains: [...value.gains] };
};
export const eqPreference = createCloudPreference<EqPreference>('equalizer', read, value => { equalizer.preset = value.preset; equalizer.gains = [...value.gains]; updateGraph(); }, validateEq);
const save = () => { const value = { preset: equalizer.preset, gains: [...equalizer.gains] }; if (eqPreference.state.storage === 'cloud') { eqPreference.changed(value); return; } try { localStorage.setItem(key, JSON.stringify(value)); } catch { equalizer.message = '当前浏览器无法保存音效设置'; } };
function updateGraph() {
  if (!context) return;
  // Leave headroom for boosted bands so the EQ does not trivially clip.
  const headroom = Math.max(0, ...equalizer.gains);
  for (const graph of graphs.values()) {
    graph.filters.forEach((filter, i) => filter.gain.setTargetAtTime(equalizer.gains[i], context!.currentTime, .02));
    graph.preamp.gain.setTargetAtTime(Math.pow(10, -headroom / 20), context.currentTime, .02);
  }
}
export function setEqualizerPreset(preset: string) {
  const selected = eqPresets[preset]; if (!selected) return;
  equalizer.preset = preset; equalizer.gains = [...selected.gains]; save(); updateGraph();
}
export function setEqualizerBand(index: number, value: number) {
  if (!Number.isInteger(index) || index < 0 || index >= eqFrequencies.length || !Number.isFinite(value)) return;
  Vue.set(equalizer.gains, index, Math.max(-12, Math.min(12, value))); equalizer.preset = 'custom'; save(); updateGraph();
}
export async function enableEqualizer() {
  try {
    context ||= new AudioContext();
    await context.resume();
    if (context.state !== 'running') throw new Error('浏览器未启动音频处理');
    equalizer.enabled = true; equalizer.message = '音效适用于本地文件和站内中转；直连播放保持原音。'; return true;
  } catch { equalizer.enabled = false; equalizer.message = '无法启动音效，继续使用原音播放。'; return false; }
}
export function disableEqualizer() { equalizer.enabled = false; equalizer.active = false; }
export function isEqualizerBound(media: HTMLAudioElement) { return bound.has(media); }
export function setEqualizerOwner(media: HTMLAudioElement) {
  owner = media; equalizer.active = graphs.has(media);
  if (equalizer.active) equalizer.message = '正在处理本地文件或站内中转音频。';
  else if (equalizer.enabled) equalizer.message = '当前歌曲使用原音播放；音效适用于本地文件和站内中转。';
}
export function detachEqualizer(media: HTMLAudioElement) {
  const graph = graphs.get(media); if (!graph) return;
  graph.source.disconnect(); graph.filters.forEach(node => node.disconnect()); graph.preamp.disconnect(); graphs.delete(media);
  if (owner === media) { owner = null; equalizer.active = false; }
}
/** Attach only to fresh disposable media whose source was classified as safe. */
export function attachLocalEqualizer(media: HTMLAudioElement, url: string, relay = false) {
  if (!equalizer.enabled || !context || context.state !== 'running' || media.getAttribute('src')) return false;
  if (relay) {
    let parsed: URL; try { parsed = new URL(url, location.href); } catch { return false; }
    if (parsed.origin !== location.origin || !/^\/api\/streams\/[A-Za-z0-9._-]+$/.test(parsed.pathname) || parsed.username || parsed.password || parsed.hash) return false;
    media.crossOrigin = 'anonymous';
  } else if (!url.startsWith(`blob:${location.origin}/`)) return false;
  const filters: BiquadFilterNode[] = [];
  let preamp: GainNode | null = null;
  let source: MediaElementAudioSourceNode | null = null;
  try {
    for (const frequency of eqFrequencies) { const node = context.createBiquadFilter(); filters.push(node); node.type = 'peaking'; node.frequency.value = frequency; node.Q.value = 1; }
    preamp = context.createGain();
    filters.slice(1).reduce<AudioNode>((previous, node) => { previous.connect(node); return node; }, filters[0]);
    filters[filters.length - 1].connect(preamp); preamp.connect(context.destination);
    source = context.createMediaElementSource(media); bound.add(media); source.connect(filters[0]);
    graphs.set(media, { media, source, filters, preamp }); updateGraph(); return true;
  } catch {
    source?.disconnect(); filters.forEach(node => node.disconnect()); preamp?.disconnect(); equalizer.message = '音效启动失败，当前播放保持不变。'; return false;
  }
}
export function markEqualizerBypass() { equalizer.active = false; if (equalizer.enabled) equalizer.message = '当前歌曲使用原音播放；音效适用于本地文件和站内中转。'; }
